/* BreadcrumbJsonLd — injects schema.org BreadcrumbList JSON-LD.
 *
 * Google surfaces this as the breadcrumb trail under the search result URL
 * ("Directory › Dinners › Grandma's Pot Roast" instead of raw
 * "recipefiler.com/r/grandmas..."). Boosts CTR by making the search result
 * feel structured and trustworthy.
 *
 * Usage:
 *   <BreadcrumbJsonLd items={[
 *     { name: "Home", url: "https://recipefiler.com/" },
 *     { name: "Directory", url: "https://recipefiler.com/directory" },
 *     { name: recipe.title },  // last item's URL omitted per spec
 *   ]} />
 *
 * The final item MUST NOT have a url (per Google's guidelines — the
 * current page is not linkable to itself).
 */
import { useEffect } from "react";

export default function BreadcrumbJsonLd({ items }) {
  useEffect(() => {
    if (!Array.isArray(items) || items.length < 2) return undefined;

    const clean = items
      .filter((i) => i && typeof i.name === "string" && i.name.trim())
      .map((i, idx, arr) => {
        const listItem = {
          "@type": "ListItem",
          position: idx + 1,
          name: i.name,
        };
        // Last item skips `item` (Google convention for the current page).
        if (idx < arr.length - 1 && i.url) {
          listItem.item = i.url;
        }
        return listItem;
      });

    const schema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: clean,
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "rf-breadcrumb-schema";
    script.text = JSON.stringify(schema);
    // Replace any prior breadcrumb from a previous page to avoid multiple
    // BreadcrumbList emissions during SPA navigation.
    const stale = document.getElementById("rf-breadcrumb-schema");
    if (stale) stale.remove();
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById("rf-breadcrumb-schema");
      if (el) el.remove();
    };
  }, [items]);

  return null;
}
